import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useLocation } from 'react-router-dom';
import { intl } from 'i18n';

import { CurriculumsApi } from 'global/api';
import {
  CurriculumDetail,
  CurriculumModules,
  ModuleHierarchy,
} from 'labxchange-client';
import { showErrorMessage, PillButton } from 'ui/components';
import { WrappedMessage, getHomeVariationExperimentValue } from 'utils';
import { searchModules } from 'curriculum/utils';
import { ModuleSearchBar } from 'curriculum/components/ModuleSearchBar';
import {
  CurriculumModuleCard,
  CurriculumModuleCardSkeleton,
} from 'curriculum/components/CurriculumModuleCard';
import { ROUTES } from 'global/constants';
import { analyticsInstance, EVENT_NAMES } from 'tracking';
import uiMessages from 'ui/components/displayMessages';

import messages from '../../displayMessages';
import HeroContentHeading from './HeroContentHeading';
import CommonContent from './CommonContent';
import { EmptySearchResults } from '../../../../curriculum/components/EmptySearchResults';

interface HomeVariationTwoProps {
  curriculumSlug: string;
  countryCode: string;
  isUserLoggedIn: boolean;
}

export const HomeVariationTwo = ({
  curriculumSlug,
  isUserLoggedIn,
  countryCode,
}: HomeVariationTwoProps) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const experimentVariation = getHomeVariationExperimentValue(curriculumSlug);

  const [loading, setLoading] = useState<boolean>(true);
  const [modulesLoading, setModulesLoading] = useState<boolean>(true);
  const [curriculum, setCurriculum] = useState<CurriculumDetail | undefined>(
    undefined
  );
  const [selectedSubject, setSelectedSubject] = useState<string>(
    queryParams.get('subject') || ''
  );
  const [selectedGrade, setSelectedGrade] = useState<string>(
    queryParams.get('grade') || ''
  );
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [curriculumModules, setCurriculumModules] = useState<any[]>([]);
  const [filteredModules, setFilteredModules] = useState<any[]>([]);

  useEffect(() => {
    const fetchCurriculumData = async () => {
      setLoading(true);
      try {
        const response: CurriculumDetail = await CurriculumsApi.getDetail({
          id: curriculumSlug,
        });
        setCurriculum(response);
        const firstSubject = response.subjects?.[0];
        if (!selectedSubject) {
          setSelectedSubject(firstSubject?.slug || '');
        }
        if (!selectedGrade) {
          setSelectedGrade(firstSubject?.grades?.[0].slug || '');
        }
      } catch (error) {
        setLoading(false);
        setCurriculum(undefined);
        showErrorMessage(
          <WrappedMessage message={messages.homeVariationPageCurriculumError} />
        );
      }
      setLoading(false);
    };

    fetchCurriculumData();
  }, [curriculumSlug]);

  useEffect(() => {
    if (!curriculum || !selectedSubject || !selectedGrade) return;
    const fetchModules = async () => {
      setModulesLoading(true);
      try {
        const response: CurriculumModules = await CurriculumsApi.read({
          id: curriculum.slug,
          subject: selectedSubject,
          grade: selectedGrade,
        });
        if (response?.modules) {
          // Map modules to include an index (starting at 1)
          const modules = response.modules.map((module, index) => ({
            ...module,
            index: index + 1,
          }));
          setCurriculumModules(modules);
        } else {
          setCurriculumModules([]);
        }
      } catch (error) {
        showErrorMessage(
          intl.formatMessage(messages.homeVariationPageModulesError)
        );
      }
      setModulesLoading(false);
    };

    fetchModules();
  }, [curriculum, selectedSubject, selectedGrade]);

  useEffect(() => {
    const results = searchModules(curriculumModules, searchKeyword);
    setFilteredModules(results);
  }, [searchKeyword, curriculumModules]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (selectedSubject) params.set('subject', selectedSubject);
    if (selectedGrade) params.set('grade', selectedGrade);
    window.history.replaceState(null, '', `?${params.toString()}`);
  }, [selectedSubject, selectedGrade]);

  const onPillClick = (subjectSlug: string, gradeSlug: string) => {
    setSelectedSubject(subjectSlug);
    setSelectedGrade(gradeSlug);
    setSearchKeyword('');
  };

  const onModuleClick = (module: ModuleHierarchy) =>
    analyticsInstance.track(
      EVENT_NAMES.NewHomePageSubModuleClicked(experimentVariation),
      {
        module_id: module.id,
        module_slug: module.slug,
        module_name: module.name,
        module_url: ROUTES.Curriculum.MODULE_SLUG(
          curriculumSlug,
          selectedSubject,
          selectedGrade,
          module.slug
        ),
        search_keyword: searchKeyword,
        selected_subject: selectedSubject,
        selected_grade: selectedGrade,
        curriculum_slug: curriculumSlug,
        country: countryCode,
        experiment_variation: experimentVariation,
        url: window.location.toString(),
      }
    );

  const renderHeroSection = () => (
    <div className='hc-var-two-hero-section'>
      <div className='bg-map' tabIndex={-1}>
        <img
          src={`/assets/images/home-bg-${curriculumSlug}-map.png`}
          alt={intl.formatMessage(messages.homeVariationPageBgMapAlt, {
            curriculumSlug,
          })}
        />
      </div>
      <div className='bg-flag' tabIndex={-1}>
        <img
          src={`/assets/images/home-bg-${curriculumSlug}-flag.png`}
          alt={intl.formatMessage(messages.homeVariationPageBgFlagAlt, {
            curriculumSlug,
          })}
        />
      </div>
      <div className='harvard-logo'>
        <img
          src='/assets/images/harvard-logo-white.svg'
          alt={intl.formatMessage(uiMessages.uiHarvardOnlineLogo)}
          title={intl.formatMessage(uiMessages.uiHarvardOnlineLogo)}
        />
      </div>
      <div className='hero-content-wrapper'>
        <div className='hero-content'>
          <HeroContentHeading countryCode={countryCode} />
          <div className='hero-content-pill-buttons'>
            {loading
              ? Array.from({ length: 4 }, (_, index) => (
                  <Skeleton
                    key={index}
                    className='pill-button'
                    width={150}
                    height={40}
                  />
                ))
              : curriculum?.subjects?.map((subject) =>
                  subject.grades?.map((grade) => {
                    if (subject.isPublic) {
                      const isSelected =
                        selectedSubject === subject.slug &&
                        selectedGrade === grade.slug;
                      return (
                        <PillButton
                          key={`${subject.slug}-${grade.slug}`}
                          label={`${subject.name} ${grade.name}`}
                          onClick={() => onPillClick(subject.slug, grade.slug)}
                          isSelected={isSelected}
                          ariaLabel={`${subject.name} ${grade.name}`}
                        />
                      );
                    }
                    return null;
                  })
                )}
          </div>
          <div className='hero-content-search'>
            {loading ? (
              <Skeleton
                data-testid='search-skeleton'
                width='100%'
                height={50}
              />
            ) : (
              <ModuleSearchBar
                value={searchKeyword}
                onChange={(value: string) => setSearchKeyword(value)}
                placeholder={messages.homeVariationPageHeroSearchPlaceholder}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Render curriculum module cards
  const renderModuleCards = () => (
    <div className='hc-var-two-curriculum-cards'>
      {modulesLoading ? (
        <>
          {[...Array(4)].map((_, index) => (
            <CurriculumModuleCardSkeleton key={index} />
          ))}
        </>
      ) : (
        filteredModules.map((curriculumModule) => (
          <CurriculumModuleCard
            key={curriculumModule.id}
            curriculumSlug={curriculumSlug}
            curriculumModule={curriculumModule}
            subject={selectedSubject}
            grade={selectedGrade}
            onModuleClick={onModuleClick}
            hideModuleNumber={curriculumSlug === 'ncert'}
          />
        ))
      )}
      {filteredModules.length === 0 && !modulesLoading && (
        <EmptySearchResults />
      )}
    </div>
  );

  return (
    <>
      {renderHeroSection()}
      {renderModuleCards()}
      <CommonContent
        isUserLoggedIn={isUserLoggedIn}
        curriculumSlug={curriculumSlug}
        countryCode={countryCode}
      />
    </>
  );
};

export default HomeVariationTwo;
